import type { AlbumSortToken } from '../types/album-sort-token'

type AlbumSortGroup = 'empty' | 'small-numeric-prefix' | 'alpha' | 'year-prefix'

const GROUP_RANK: Record<AlbumSortGroup, number> = {
  'small-numeric-prefix': 0,
  alpha: 1,
  'year-prefix': 2,
  empty: 3,
}

/** Year-sized leading numbers (e.g. 2024/2025) sort after alphabetic names. */
const YEAR_PREFIX_THRESHOLD = 1000

/** ASCII code-point tie-break when case-insensitive token comparison is equal. */
function compareAsciiTieBreak(a: string, b: string): number {
  const length = Math.min(a.length, b.length)

  for (let i = 0; i < length; i++) {
    const diff = a.charCodeAt(i) - b.charCodeAt(i)

    if (diff !== 0) return diff
  }

  return a.length - b.length
}

export function tokenizeAlbumName(displayName: string): AlbumSortToken[] {
  let value = displayName.trim()

  // Keep strip set aligned with album-search / filename-search (incl. smart quotes).
  value = value.replace(/['\u2018\u2019\u02BC´`!#()]/g, '')
  value = value.replace(/[&.,\-/]/g, ' ')
  value = value.replace(/\s+/g, ' ').trim()

  if (!value) return []

  return value.split(' ').map((part) => {
    if (/^\d+$/.test(part)) {
      return { type: 'numeric', value: Number.parseInt(part, 10) }
    }

    return { type: 'alpha', value: part, original: part }
  })
}

function getAlbumSortGroup(tokens: AlbumSortToken[]): AlbumSortGroup {
  if (tokens.length === 0) return 'empty'

  const first = tokens[0]

  if (first.type === 'numeric' && first.value >= YEAR_PREFIX_THRESHOLD) {
    return 'year-prefix'
  }

  if (first.type === 'numeric') return 'small-numeric-prefix'
  return 'alpha'
}

function compareSortTokens(a: AlbumSortToken, b: AlbumSortToken): number {
  if (a.type === 'numeric' && b.type === 'numeric') {
    return a.value - b.value
  }

  if (a.type === 'numeric' && b.type === 'alpha') return -1
  if (a.type === 'alpha' && b.type === 'numeric') return 1

  if (a.type === 'alpha' && b.type === 'alpha') {
    const insensitive = a.value.localeCompare(b.value, undefined, { sensitivity: 'base' })

    if (insensitive !== 0) return insensitive
    return compareAsciiTieBreak(a.original, b.original)
  }

  return 0
}

export function compareAlbumDisplayNames(a: string, b: string): number {
  const tokensA = tokenizeAlbumName(a)
  const tokensB = tokenizeAlbumName(b)

  const groupA = getAlbumSortGroup(tokensA)
  const groupB = getAlbumSortGroup(tokensB)
  const groupCmp = GROUP_RANK[groupA] - GROUP_RANK[groupB]

  if (groupCmp !== 0) return groupCmp

  if (groupA === 'empty') return 0

  const maxLength = Math.max(tokensA.length, tokensB.length)

  for (let i = 0; i < maxLength; i++) {
    const tokenA = tokensA[i]
    const tokenB = tokensB[i]

    if (!tokenA && tokenB) return -1
    if (tokenA && !tokenB) return 1
    if (!tokenA || !tokenB) continue

    const tokenCmp = compareSortTokens(tokenA, tokenB)

    if (tokenCmp !== 0) return tokenCmp
  }

  return compareAsciiTieBreak(a, b)
}

/** Stable sort: equal normalized keys keep server order. */
export function stableSortAlbumsByDisplayName<T extends { displayName: string }>(items: T[]): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const cmp = compareAlbumDisplayNames(left.item.displayName, right.item.displayName)

      if (cmp !== 0) return cmp
      return left.index - right.index
    })
    .map(({ item }) => item)
}
